import { useEffect, useState } from 'react';
import {
  Button,
  Box,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useAlertStore } from 'src/stores/AlertStore/AlertStore';
import {
  UNHANDLED_ERROR,
  USER_DETAILS_NOT_FOUND_ERROR,
} from 'src/constants/errors';
import { useAuthStore } from 'src/stores/AuthStore/AuthStore';
import ErrorBoundary from '../error-boundary/ErrorBoundry';
import CloseIcon from '@mui/icons-material/Close';
import CustomLoading from '../custom-loading';
import { ROLES } from 'src/constants/Role';
import { Formik, Form } from 'formik';
import { sendUpgradeRequest } from 'src/services/agendaJobs';
import { CustomSelect } from '../custom-select-field/CustomSelectMUIComponent';
import { CustomCheckBoxWithText } from '../custom-checkbox-field/CustomCheckBoxMUIComponent';
import { useUpgradeRequestStore } from 'src/stores/UpgradeRequestStore/UpgradeRequestStore';
import {
  UpgradeRequestFormFields,
  UpgradeRequestPayload,
} from 'src/types/upgradeRequest';
import { SEND_UPGRADE_REQUEST_SUCCESS } from 'src/constants/upgradeRequest';
import {
  getCombinedNotifyUserList,
  validateUpgradeRequest,
} from 'src/utils/upgradeRequest';

export const upgradeRequestAllowedRoles: Array<keyof typeof ROLES> = [
  ROLES.USER,
  ROLES.ADMIN,
  ROLES.SUPER_ADMIN,
  ROLES.DOMAIN_OWNER,
];

const UpgradeRequestPopup: React.FC = observer(() => {
  const [loading, setLoading] = useState(false);
  const { notify } = useAlertStore();
  const { getUserDetails } = useAuthStore();
  const userDetails = getUserDetails();
  const {
    usersWithUserRole,
    usersWithAdminRole,
    isUpgradeRequestPopupOpen,
    closeUpgradeRequestPopUp,
  } = useUpgradeRequestStore();
  const hasUsersWithUserRole = usersWithUserRole.length > 0;
  const hasUsersWithAdminRole = usersWithAdminRole.length > 0;

  const initialValuesForUpgradeRequestForm: UpgradeRequestFormFields = {
    isNotifyAdmin: hasUsersWithAdminRole,
    notifyUsers: [],
  };

  const handleSendUpgradeNotification = async (
    values: UpgradeRequestFormFields,
  ) => {
    if (!userDetails) {
      return;
    }

    const body: UpgradeRequestPayload = {
      notifyUsersList: [],
    };

    if (!validateUpgradeRequest(values)) {
      return;
    }

    const combinedNotifyUserList = getCombinedNotifyUserList(
      values,
      usersWithUserRole,
      usersWithAdminRole,
    );

    body['notifyUsersList'] = combinedNotifyUserList;
    setLoading(true);

    const {
      success: scheduleUpgradeNotificationSuccess,
      content: scheduleUpgradeNotificationResponse,
    } = await sendUpgradeRequest(body);

    if (
      !scheduleUpgradeNotificationSuccess ||
      !scheduleUpgradeNotificationResponse
    ) {
      notify(UNHANDLED_ERROR, 'error');
      setLoading(false);

      return;
    }

    setLoading(false);
    notify(SEND_UPGRADE_REQUEST_SUCCESS, 'success');
  };

  useEffect(() => {
    if (!userDetails) return;

    if (!upgradeRequestAllowedRoles.includes(userDetails.role)) {
      closeUpgradeRequestPopUp();
    }
  }, []);

  if (!userDetails) {
    return <ErrorBoundary message={USER_DETAILS_NOT_FOUND_ERROR} />;
  }

  if (loading) {
    return (
      <Dialog
        open={isUpgradeRequestPopupOpen}
        sx={{
          '& .MuiPaper-root': {
            width: '100vw',
            height: '100%',
          },
        }}
      >
        <DialogContent
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            top: '50%',
            left: '50%',
            zIndex: 1,
            opacity: 0.5,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <CustomLoading />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={isUpgradeRequestPopupOpen}
      PaperProps={{
        sx: {
          width: '100%',
        },
      }}
    >
      <DialogContent>
        <IconButton
          aria-label="close"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            cursor: 'pointer',
          }}
          onClick={closeUpgradeRequestPopUp}
        >
          <CloseIcon />
        </IconButton>
        <Box>
          <Typography
            fontWeight="bold"
            variant="h4"
            component="h3"
            fontSize="24px"
            sx={{
              marginY: '16px',
            }}
          >
            Request Upgrade
          </Typography>
          <Formik
            initialValues={initialValuesForUpgradeRequestForm}
            onSubmit={handleSendUpgradeNotification}
          >
            {({ isSubmitting, values }) => {
              return (
                <Form
                  style={{
                    gap: 2,
                  }}
                >
                  {hasUsersWithUserRole && (
                    <CustomSelect
                      name="notifyUsers"
                      label="Notify users"
                      options={usersWithUserRole.map(({ _id, email }) => ({
                        id: _id,
                        name: email,
                      }))}
                      isMultipleSelect={true}
                    />
                  )}
                  {hasUsersWithAdminRole && (
                    <CustomCheckBoxWithText
                      name="isNotifyAdmin"
                      text="Notify Admins"
                      labelPlacement="end"
                    />
                  )}

                  <Button
                    id="upgrade-request-sent"
                    variant="contained"
                    type="submit"
                    style={{
                      display: 'block',
                      marginTop: '20px',
                      marginLeft: 'auto',
                      alignSelf: 'flex-end',
                    }}
                    disabled={isSubmitting || !validateUpgradeRequest(values)}
                  >
                    SEND
                  </Button>
                </Form>
              );
            }}
          </Formik>
        </Box>
      </DialogContent>
    </Dialog>
  );
});

export default UpgradeRequestPopup;
